import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import "./styles.css";

const API_URL = process.env.REACT_APP_API_URL;

const CaseDetails = () => {
  const [pdfFiles, setPdfFiles] = useState([]);
  const { id } = useParams();

  const [caseData, setCaseData] = useState(null);
  const [caseResponses, setCaseResponses] = useState(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showShareModal, setShowShareModal] = useState(false);
  const [targetUsername, setTargetUsername] = useState("");
  const [sharingStatus, setSharingStatus] = useState(null);
  const [sharedWith, setSharedWith] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);

  // --------------------------
  // Utils
  // --------------------------
  const formatDate = (timestamp) => {
    if (!timestamp) return "Unknown date";
    const date = new Date(timestamp);
    return date.toLocaleDateString("en-US", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const groupResponsesByCategory = (responses) => {
    if (!responses || !responses.results) return {};

    const grouped = {};
    const normalizeMap = {
      environmental: "environmental",
      social: "social",
      modernization: "modernization",
      leadtime: "leadTime",
      "lead-time": "leadTime",
      lead_time: "leadTime",
      quality: "quality",
      cost: "cost",
      clientconsumer: "clientConsumer",
      "client-consumer": "clientConsumer",
      client_consumer: "clientConsumer",
    };

    responses.results.forEach((r) => {
      if (!r) return;

      let cat = (r.category || "").toLowerCase();
      cat = normalizeMap[cat] || cat;

      const section = r.section || `${cat}-1`;

      if (!grouped[cat]) grouped[cat] = {};
      if (!grouped[cat][section]) grouped[cat][section] = [];
      grouped[cat][section].push(r);
    });

    return grouped;
  };

  // --------------------------
  // Fetch helpers
  // --------------------------
  const fetchAllResponseSources = async (enterpriseId) => {
    const token = localStorage.getItem("token") || "";

    let resultsResponses = [];
    let evaluationResponses = [];

    // --- 1. RESULTS ------------------------
    const sources = [
      `/api/enterprise-results/${enterpriseId}`,
      `/api/all-results/${enterpriseId}`,
      `/api/direct-results/${enterpriseId}`,
    ];

    for (const url of sources) {
      try {
        const response = await fetch(`${API_URL}${url}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.ok) {
          const data = await response.json().catch(() => []);
          if (Array.isArray(data)) resultsResponses.push(...data);
        }
      } catch {}
    }

    // --- 1b. Lean results also count as RESULTS ---
    try {
      const storedCaseId = localStorage.getItem("caseId") || "";

      if (storedCaseId) {
        const response = await fetch(`${API_URL}/api/lean/results/${storedCaseId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.ok) {
          const data = await response.json().catch(() => null);
          if (data?.results) {
            for (const category in data.results) {
              const arr = data.results[category] || [];
              resultsResponses.push(
                ...arr.map((item) => ({
                  ...item,
                  category: category.toLowerCase(),
                  caseId: storedCaseId,
                }))
              );
            }
          }
        }
      }
    } catch {}

    // --- 2. EVALUATION (une seule fois) ------------------------
    try {
      const response = await fetch(`${API_URL}/api/evaluation-results/${enterpriseId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json().catch(() => []);
        if (Array.isArray(data)) evaluationResponses.push(...data);
      }
    } catch {}

    // ---- REMOVE DUPLICATES IN RESULTS ----
    resultsResponses = resultsResponses.filter(
      (item, index, self) =>
        index ===
        self.findIndex(
          (r) =>
            r.question === item.question &&
            r.category === item.category &&
            r.section === item.section
        )
    );

    // ---- REMOVE DUPLICATES IN EVALUATION ----
    evaluationResponses = evaluationResponses.filter(
      (item, index, self) =>
        index ===
        self.findIndex(
          (r) =>
            r.question === item.question &&
            r.category === item.category &&
            r.section === item.section
        )
    );

    return { resultsResponses, evaluationResponses };
  };

  // --------------------------
  // 1) Fetch Case Details
  // --------------------------
  useEffect(() => {
    const fetchCaseDetails = async (caseIdParam) => {
      const caseId = caseIdParam || id;
      setLoading(true);
      setError(null);

      try {
        const token = localStorage.getItem("token");
        if (!token) {
          setError("Error 401: Not authenticated (no token found)");
          setLoading(false);
          return;
        }

        const response = await fetch(`${API_URL}/api/case-details/${caseId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          const txt = await response.text().catch(() => "");
          setError(`Error ${response.status}: ${txt || response.statusText}`);
          setLoading(false);
          return;
        }

        const data = await response.json();
        if (data?.found && data?.case) {
          const storedId = data.case._id; // âœ… dâ€™abord on définit

          setCaseData(data.case);
          setSharedWith(data.case.shared_with || []);

          // âœ… active case (compat + standard)
          localStorage.setItem("caseId", storedId);
          localStorage.setItem("active_case_id", storedId);

          // âœ… enterprise actif
          if (data.case.enterpriseId) {
            localStorage.setItem("active_enterprise_id", data.case.enterpriseId);

            const responses = await fetchAllResponseSources(data.case.enterpriseId);
            setCaseResponses({
              found: true,
              results: responses.resultsResponses,
              evaluation: responses.evaluationResponses,
            });
          } else {
            setCaseResponses({ found: false, results: [], evaluation: [] });
          }
        } else {
          setError("Case not found or invalid data");
        }
      } catch (err) {
        console.error("Error fetching case details:", err);
        setError(`Error: ${err?.message || String(err)}`);
      } finally {
        setLoading(false);
      }
    };

    if (!id) {
      const storedCaseId = localStorage.getItem("caseId");
      if (storedCaseId) fetchCaseDetails(storedCaseId);
      else setError("No case selected. Please open one from the list.");
    } else {
      // compat
      localStorage.setItem("caseId", id);
      localStorage.setItem("active_case_id", id);
      fetchCaseDetails(id);
    }
  }, [id]);

  // --------------------------
  // 2) Default active tab
  // --------------------------
  useEffect(() => {
    if (caseResponses?.results?.length > 0) {
      const groupedData = groupResponsesByCategory(caseResponses);
      const categories = Object.keys(groupedData);
      if (categories.length > 0 && !activeCategory) setActiveCategory(categories[0]);
    }
  }, [caseResponses, activeCategory]);

  // --------------------------
  // 3) Fetch PDFs linked to case
  // --------------------------
  useEffect(() => {
    const fetchCasePdfs = async () => {
      try {
        if (!id) return;

        const response = await fetch(`${API_URL}/api/case/${id}/pdfs`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
          },
        });

        const data = await response.json().catch(() => null);
        if (data?.success && Array.isArray(data.pdfs)) setPdfFiles(data.pdfs);
        else setPdfFiles([]);
      } catch (err) {
        console.error("Error fetching PDFs:", err);
        setPdfFiles([]);
      }
    };

    fetchCasePdfs();
  }, [id]);

  // --------------------------
  // Share / Unshare
  // --------------------------
  const handleShareCase = async (e) => {
    e.preventDefault();
    if (!targetUsername.trim()) {
      setSharingStatus({ success: false, message: "Please enter a valid username" });
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/share-case/${id}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ username: targetUsername }),
      });

      const data = await response.json().catch(() => null);
      if (response.ok && data?.success) {
        setSharingStatus({ success: true, message: data.message });
        setSharedWith([...sharedWith, targetUsername]);
        setTargetUsername("");
      } else {
        setSharingStatus({ success: false, message: data?.message || "Error sharing case" });
      }
    } catch (err) {
      setSharingStatus({ success: false, message: `Error sharing case: ${err?.message || err}` });
    }
  };

  const handleUnshareCase = async (username) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/share-case/${id}/${username}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });

      const data = await response.json().catch(() => null);
      if (response.ok && data?.success) {
        setSharedWith(sharedWith.filter((u) => u !== username));
        setSharingStatus({ success: true, message: data.message });
      } else {
        setSharingStatus({ success: false, message: data?.message || "Error removing share" });
      }
    } catch (err) {
      setSharingStatus({ success: false, message: `Error removing share: ${err?.message || err}` });
    }
  };

  // --------------------------
  // Export CSV
  // --------------------------
  const exportResponsesToCSV = () => {
    if (!caseResponses?.results?.length) {
      alert("No data to export");
      return;
    }

    let csvContent = "Category,Question,Answer,Date\n";
    caseResponses.results.forEach((r) => {
      const category = r.category || "Uncategorized";
      const question = (r.question || "").replace(/,/g, ";");
      const answer = (r.answer || "").replace(/,/g, ";");
      const date = r.timestamp ? new Date(r.timestamp).toLocaleString() : "";
      csvContent += `"${category}","${question}","${answer}","${date}"\n`;
    });

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `case_${id}_responses.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // --------------------------
  // UI states
  // --------------------------
  if (loading) {
    return (
      <div className="case-details-container loading">
        <div className="loader" />
        <p>Loading case details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="case-details-container error">
        <h2>Error</h2>
        <p>{error}</p>
        <Link to="/existing-case" className="back-button">
          Back to case list
        </Link>
      </div>
    );
  }

  if (!caseData) {
    return (
      <div className="case-details-container error">
        <h2>Case not found</h2>
        <p>The details for this case are not available.</p>
        <Link to="/existing-case" className="back-button">
          Back to case list
        </Link>
      </div>
    );
  }

  const groupedResponses = caseResponses ? groupResponsesByCategory(caseResponses) : {};
  const groupedEvaluation =
    caseResponses?.evaluation?.length > 0
      ? groupResponsesByCategory({ results: caseResponses.evaluation })
      : {};

  const ShareModal = () => (
    <div className="share-modal-overlay">
      <div className="share-modal">
        <div className="share-modal-header">
          <h3>Share this Case</h3>
          <button className="close-button" onClick={() => setShowShareModal(false)}>
            à—
          </button>
        </div>

        {sharingStatus && (
          <div className={`sharing-status ${sharingStatus.success ? "success" : "error"}`}>
            {sharingStatus.message}
          </div>
        )}

        <form onSubmit={handleShareCase} className="share-form">
          <div className="form-group">
            <label htmlFor="target-username">Username:</label>
            <input
              type="text"
              id="target-username"
              value={targetUsername}
              onChange={(e) => setTargetUsername(e.target.value)}
              placeholder="Enter username"
            />
          </div>
          <button type="submit" className="share-button">
            Share
          </button>
        </form>

        {sharedWith?.length > 0 && (
          <div className="shared-with-list">
            <h4>Shared with:</h4>
            <ul>
              {sharedWith.map((username, index) => (
                <li key={index} className="shared-user">
                  <span>{username}</span>
                  <button className="unshare-button" onClick={() => handleUnshareCase(username)}>
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="case-details-container">
      {showShareModal && <ShareModal />}

      <div className="case-details-header">
        <h1>Case Details</h1>
        <div className="header-buttons">
          <button className="share-button" onClick={() => setShowShareModal(true)}>
            Share
          </button>
          <Link to="/existing-case" className="back-button">
            Back to case list
          </Link>
        </div>
      </div>

      <div className="case-details-card">
        <h2>{caseData.company_name || "Unnamed Company"}</h2>

        <div className="case-info-section">
          <h3>General Information</h3>
          <div className="info-grid">
            <div className="info-item">
              <label>Creation date:</label>
              <span>{formatDate(caseData.timestamp || caseData.date_created)}</span>
            </div>
            <div className="info-item">
              <label>Activity sector:</label>
              <span>{caseData.activity_sector || "Not specified"}</span>
            </div>
            <div className="info-item">
              <label>Company size:</label>
              <span>{caseData.company_size || "Not specified"} employees</span>
            </div>
            {caseData.enterpriseId && (
              <div className="info-item">
                <label>Enterprise ID:</label>
                <span>{caseData.enterpriseId}</span>
              </div>
            )}
          </div>
        </div>

        <div className="case-info-section">
          <h3>PDF Files Linked to This Case</h3>
          {pdfFiles.length > 0 ? (
            <ul style={{ listStyle: "none", paddingLeft: 0 }}>
              {pdfFiles.map((pdf, index) => (
                <li key={index} style={{ marginBottom: "10px" }}>
                  <a
                    href={`${API_URL}/api/pdf/${pdf._id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "#0066cc", textDecoration: "underline" }}
                  >
                    {pdf.filename}
                  </a>
                  <span style={{ marginLeft: "10px", color: "#666", fontSize: "0.9em" }}>
                    {pdf.uploaded_at ? new Date(pdf.uploaded_at).toLocaleString() : "Unknown date"}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p style={{ color: "#777" }}>No PDF files imported for this case yet.</p>
          )}
        </div>

        {caseResponses?.found && (
          <div className="case-responses-section">
            <div className="responses-header">
              <h3>Acquisition Details</h3>
              <button className="export-button" onClick={exportResponsesToCSV} title="Export responses as CSV">
                Export CSV
              </button>
            </div>

            {Object.keys(groupedResponses).length === 0 ? (
              <p className="no-responses">No responses available for this case</p>
            ) : (
              <div className="categories-container">
                <div className="categories-tabs">
                  {["environmental", "social", "modernization", "leadTime", "cost", "quality", "clientConsumer"].map(
                    (category) => {
                      const hasResponses = groupedResponses[category] && Object.keys(groupedResponses[category]).length > 0;

                      return (
                        <div
                          key={category}
                          className={`category-tab ${activeCategory === category ? "active" : ""} ${
                            !hasResponses ? "disabled" : ""
                          }`}
                          onClick={() => hasResponses && setActiveCategory(category)}
                        >
                          {category}
                          {!hasResponses && " (empty)"}
                        </div>
                      );
                    }
                  )}
                </div>

                <div className="category-content">
                  {activeCategory &&
                    groupedResponses[activeCategory] &&
                    Object.entries(groupedResponses[activeCategory]).map(([section, responses]) => (
                      <div className="section-container" key={section}>
                        <h5 className="section-title">{section}</h5>

                        <div className="responses-list">
                          {responses.map((response, index) => (
                            <div className="response-item" key={index}>
                              <div className="response-question">{response.question}</div>
                              <div className={`response-answer ${response.answer === "Yes" ? "positive" : response.answer === "No" ? "negative" : "neutral"}`}>
                                {response.answer}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
        )}

        {caseResponses?.evaluation?.length > 0 && (
          <div className="case-responses-section">
            <div className="responses-header">
              <h3>Evaluation Details</h3>
            </div>

            {Object.keys(groupedEvaluation).length === 0 ? (
              <p className="no-responses">No evaluation data available</p>
            ) : (
              <div className="categories-container">
                <div className="categories-tabs">
                  {["environmental", "social", "modernization", "leadTime", "cost", "quality", "clientConsumer"].map(
                    (category) => {
                      const hasResponses = groupedEvaluation[category] && Object.keys(groupedEvaluation[category]).length > 0;

                      return (
                        <div
                          key={category}
                          className={`category-tab ${activeCategory === category ? "active" : ""} ${
                            !hasResponses ? "disabled" : ""
                          }`}
                          onClick={() => hasResponses && setActiveCategory(category)}
                        >
                          {category}
                          {!hasResponses && " (empty)"}
                        </div>
                      );
                    }
                  )}
                </div>

                <div className="category-content">
                  {activeCategory &&
                    groupedEvaluation[activeCategory] &&
                    Object.entries(groupedEvaluation[activeCategory]).map(([section, responses]) => (
                      <div className="section-container" key={section}>
                        <h5 className="section-title">{section}</h5>

                        <div className="responses-list">
                          {responses.map((response, index) => (
                            <div className="response-item" key={index}>
                              <div className="response-question">{response.question}</div>
                              <div className="response-answer neutral">{response.answer}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="case-actions">
          <Link to={`/analysisbeta?caseId=${id}`} className="action-button">
            View full analysis
          </Link>
        </div>
      </div>
    </div>
  );
};

export default CaseDetails;
